import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI } from '../../services/api';
import StatCard from '../../components/common/StatCard';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const STAGE_COLORS = {
  identified:    theme.info,
  qualification: theme.warning,
  proposal:      '#8b5cf6',
  negotiation:   '#06b6d4',
  won:           theme.success,
  lost:          theme.danger,
  on_hold:       theme.textMuted,
};

const STAGE_LABELS = {
  identified:    'Identified',
  qualification: 'Qualification',
  proposal:      'Proposal',
  negotiation:   'Negotiation',
  won:           'Won',
  lost:          'Lost',
  on_hold:       'On Hold',
};

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([salesAPI.getDashboard(), salesAPI.getAnalytics()])
      .then(([d, a]) => { setDashboard(d.data); setAnalytics(a.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
      <div style={{ width: '36px', height: '36px', border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!dashboard) return null;

  const fmt = (n) => n >= 10000000
    ? `₹${(n / 10000000).toFixed(1)}Cr`
    : n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : `₹${(n || 0).toLocaleString()}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Sales Dashboard</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Tender pipeline and performance overview</p>
        </div>
        <Button onClick={() => navigate('/sales/tenders')}>+ New Tender</Button>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <StatCard icon="📋" label="Total Tenders"  value={dashboard.total}            color={theme.info} />
        <StatCard icon="🏆" label="Won"             value={dashboard.won}              color={theme.success} />
        <StatCard icon="⚙️" label="In Progress"     value={dashboard.in_progress}      color={theme.primary} />
        <StatCard icon="📈" label="Win Rate"         value={`${dashboard.win_ratio}%`} color='#8b5cf6' />
        <StatCard icon="💰" label="Pipeline Value"   value={fmt(dashboard.pipeline_value)} color={theme.warning} />
        <StatCard icon="✅" label="Won Value"         value={fmt(dashboard.won_value)}  color={theme.success} />
      </div>

      {/* Pipeline Stages */}
      <Card title="Pipeline by Stage">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {Object.entries(dashboard.stage_counts || {}).map(([stage, count]) => (
            <div
              key={stage}
              onClick={() => navigate(`/sales/tenders?status=${stage}`)}
              style={{
                flex: '1 1 100px',
                padding: '1rem',
                borderRadius: '0.75rem',
                background: STAGE_COLORS[stage] || theme.textMuted,
                color: '#fff',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>{count}</div>
              <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.9 }}>{STAGE_LABELS[stage] || stage}</div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Upcoming Deadlines */}
        <Card title="Upcoming Deadlines (7 days)">
          {!dashboard.upcoming_deadlines?.length ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No upcoming deadlines</p>
          ) : dashboard.upcoming_deadlines.map(t => (
            <div
              key={t.id}
              onClick={() => navigate(`/sales/tenders/${t.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text }}>{t.title}</span>
              <span style={{ fontSize: '0.75rem', color: theme.danger, fontWeight: 500, whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>
                {new Date(t.submission_deadline).toLocaleDateString('en-IN')}
              </span>
            </div>
          ))}
        </Card>

        {/* Pending Approvals */}
        <Card title="Pending Approvals">
          {!dashboard.pending_approvals?.length ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No pending approvals</p>
          ) : dashboard.pending_approvals.map(t => (
            <div
              key={t.id}
              onClick={() => navigate(`/sales/tenders/${t.id}`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
            >
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text }}>{t.title}</span>
              <span style={{ fontSize: '0.72rem', background: '#fef3c7', color: '#92400e', padding: '0.2rem 0.6rem', borderRadius: '999px', fontWeight: 500 }}>Pending</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Analytics */}
      {analytics && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <BarChart title="By Sector"  data={analytics.sector_data}  labelKey="sector" />
          <BarChart title="By Portal"  data={analytics.portal_data}  labelKey="portal" />
        </div>
      )}
    </div>
  );
}

function BarChart({ title, data, labelKey }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <Card title={title}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {data.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, width: '110px', flexShrink: 0, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item[labelKey]}
            </span>
            <div style={{ flex: 1, background: theme.border, borderRadius: '999px', height: '20px', overflow: 'hidden' }}>
              <div style={{ width: `${(item.count / max) * 100}%`, height: '100%', background: theme.primary, borderRadius: '999px', transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.text, width: '24px', textAlign: 'right' }}>{item.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
